import React from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';
import * as turf from '@turf/turf';
import { useSelector } from 'react-redux';
import _ from 'underscore';
import { MakeMap } from './utils';

mapboxgl.accessToken =
    'pk.eyJ1IjoiZW52aW9pZCIsImEiOiJja3g0bWx5NjgwN3MwMm9wYXB0cnpxdm43In0.F4GEDaOI0_o6fwWkL2r0lQ';

const MapManifestCreate = ({ className, status, history }) => {
    const mapContainer = React.useRef(null);
    const map = React.useRef(null);
    const StateOrigin = useSelector((state) => state?.Map.origin);
    const StateDestinations = useSelector((state) => state?.Map.destinations);
    const [geoJSON, setGeoJSON] = React.useState([]);
    const [isReady, setIsReady] = React.useState(false);
    const [routeID, setRouteID] = React.useState([]);

    React.useEffect(() => {
        if (map?.current) return;

        setIsReady(false);
        map.current = new mapboxgl.Map(MakeMap(mapContainer?.current, null, true));
    });

    const setOrigin = (location) => {
        if (!location) return;

        const des = `<strong>${location?.name}</strong><p>${location?.address
            }</p><p>${`${location?.region?.village_name}, ${location?.region?.district_name},${location?.region?.regency_name}, ${location?.region?.province_name}</p>`}`;

        const json = {
            type: 'Feature',
            properties: {
                description: des,
            },
            geometry: {
                type: 'Point',
                coordinates: [location?.longitude, location?.latitude],
            },
        };

        geoJSON?.push(json);

        // need to reorder the data
        let result = [];
        geoJSON.forEach((dest, i) => {
            dest.properties.index = i;

            result.push(dest);
        });

        setGeoJSON(result);
    };

    const setup = () => {
        if (!map?.current || geoJSON.length === 0 || !isReady) return;

        routeID?.forEach((route) => {
            const layer = map.current.getLayer(route);
            const source = map.current.getSource(route);

            if (typeof layer !== 'undefined') {
                map.current.removeLayer(route);
            }

            if (typeof source !== 'undefined') {
                map.current.removeSource(route);
            }
        });

        setRouteID([]);

        const source = map.current.getSource('places');
        const layer = map.current.getLayer('places');
        const layerS = map.current.getLayer('places-symbol');

        if (typeof layer !== 'undefined') {
            map.current.removeLayer('places');
        }

        if (typeof layerS !== 'undefined') {
            map.current.removeLayer('places-symbol');
        }

        if (typeof source !== 'undefined') {
            map.current.removeSource('places');
        }

        const json = {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: geoJSON,
            },
        };

        map.current?.addSource('places', json);
        map.current.addLayer({
            id: 'places',
            type: 'circle',
            source: 'places',
            paint: {
                'circle-color': '#4264fb',
                'circle-radius': 10,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
            },
        });

        map.current.addLayer({
            id: 'places-symbol',
            type: 'symbol',
            source: 'places',
            layout: {
                'text-field': ['get', 'index'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12,
            },
            paint: {
                'text-color': '#ffffff',
            },
        });
    };

    const animated = () => {
        if (!map?.current || geoJSON.length === 0 || !isReady) return;

        let bounds = new mapboxgl.LngLatBounds();
        _.each(geoJSON, function (json, key) {
            bounds.extend(json.geometry.coordinates)
        });

        map?.current.fitBounds(bounds, {
            padding: 100
        });
    };

    const draw = () => {
        if (!map?.current || geoJSON.length === 0 || !isReady) return;

        const length = geoJSON.length;

        if (length < 2) return;

        geoJSON?.forEach((loc, i) => {
            if (typeof geoJSON[i + 1] !== 'undefined') {
                const dest = geoJSON[i + 1];
                const pointX = loc.geometry.coordinates;
                const pointY = dest.geometry.coordinates;

                makeLines(
                    pointX,
                    pointY,
                    `route-${loc.properties.index}-${dest.properties.index}`
                );
            }
        });
    };

    const addRoute = (id) => {
        const layer = map.current.getLayer(id);
        const source = map.current.getSource(id);

        if (typeof layer !== 'undefined') {
            map.current.removeLayer(id);
        }

        if (typeof source !== 'undefined') {
            map.current.removeSource(id);
        }

        let results = routeID;

        if (routeID?.length > 0) {
            results.push(id);
        } else {
            results = [id];
        }

        setRouteID(results);
    };

    const makeLines = (pointX, pointY, route_id) => {
        const radius = turf?.rhumbDistance(pointX, pointY);
        const midpoint = turf?.midpoint(pointX, pointY);
        const bearing = turf?.rhumbBearing(pointX, pointY) - 90;
        const tori = turf?.rhumbDestination(midpoint, radius, bearing);

        const curvedLine = turf?.lineArc(
            tori,
            turf?.distance(tori, pointX),
            turf?.bearing(tori, pointY),
            turf?.bearing(tori, pointX),
            { steps: 128 }
        );

        addRoute(route_id);

        map?.current?.addSource(route_id, {
            type: 'geojson',
            data: curvedLine,
        });

        map?.current?.addLayer({
            id: route_id,
            source: route_id,
            type: 'line',
            layout: {
                'line-cap': 'round',
                'line-join': 'round',
            },
            paint: {
                'line-width': 1,
                'line-color': '#007cbf',
            },
        });
    };

    React.useEffect(() => {
        if (!map?.current) return;
        setGeoJSON([]);

        map?.current.on('load', () => {
            setIsReady(true);
        });

        map?.current.on('click', 'places', (e) => {
            // Copy coordinates array.
            const coordinates = e.features[0].geometry.coordinates.slice();
            const description = e.features[0].properties.description;

            // Ensure that if the map is zoomed out such that multiple
            // copies of the feature are visible, the popup appears
            // over the copy being pointed to.
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }

            new mapboxgl.Popup()
                .setLngLat(coordinates)
                .setHTML(description)
                .addTo(map?.current);
        });

        // Change the cursor to a pointer when the mouse is over the places layer.
        map?.current.on('mouseenter', 'places', () => {
            map.current.getCanvas().style.cursor = 'pointer';
        });

        // Change it back a pointer when it leaves.
        map?.current.on('mouseleave', 'places', () => {
            map.current.getCanvas().style.cursor = '';
        });

        setOrigin(StateOrigin);

        if (StateDestinations) {
            StateDestinations?.forEach((dest, i) => {
                setOrigin(dest);
            });
        }

        setup();
        draw();
        animated();
        setGeoJSON([]);

        return () => setGeoJSON([]);
    }, [StateOrigin, StateDestinations, history]);

    return (
        <div>
            <div
                ref={mapContainer}
                className={className ? className : 'map-container'}
            />
        </div>
    );
};

export default MapManifestCreate;
